import React from 'react';
import { useNavigate } from 'react-router-dom';

const LAST_UPDATED = 'May 2025';

const Card = ({ children, style = {} }) => (
  <div style={{ background: '#ffffff', border: '1px solid #E5E9F0', borderRadius: '1rem', padding: '1.75rem', boxShadow: '0 1px 4px rgba(0,0,0,0.04)', ...style }}>
    {children}
  </div>
);

const Section = ({ title, children }) => (
  <div style={{ marginBottom: '1.75rem' }}>
    <h2 style={{ color: '#0D1F3C', fontSize: '1.125rem', fontWeight: 700, marginBottom: '0.75rem' }}>{title}</h2>
    <div style={{ color: '#374151', lineHeight: 1.75, fontSize: '0.9375rem' }}>{children}</div>
  </div>
);

const P = ({ children }) => <p style={{ marginBottom: '0.875rem' }}>{children}</p>;

const BulletList = ({ items }) => (
  <ul style={{ listStyle: 'none', padding: 0, margin: '0.5rem 0 1rem' }}>
    {items.map((item, i) => (
      <li key={i} style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-start', marginBottom: '0.5rem' }}>
        <span style={{ color: '#1A6FD6', fontWeight: 700, flexShrink: 0, marginTop: '0.1rem' }}>—</span>
        <span>{item}</span>
      </li>
    ))}
  </ul>
);

const Highlight = ({ title, children, type = 'info' }) => {
  const styles = {
    info:    { bg: '#EFF6FF', border: '#BFDBFE', icon: 'ℹ', iconColor: '#1A6FD6' },
    success: { bg: '#F0FDF4', border: '#BBF7D0', icon: '✓', iconColor: '#16A34A' },
    warning: { bg: '#FFFBEB', border: '#FDE68A', icon: '!', iconColor: '#D97706' },
  };
  const s = styles[type];
  return (
    <div style={{ background: s.bg, border: `1px solid ${s.border}`, borderRadius: '0.75rem', padding: '1rem 1.25rem', marginBottom: '1.25rem' }}>
      <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-start' }}>
        <span style={{ color: s.iconColor, fontWeight: 700, fontSize: '1rem', flexShrink: 0, marginTop: '0.1rem' }}>{s.icon}</span>
        <div>
          {title && <p style={{ fontWeight: 700, color: '#0D1F3C', marginBottom: '0.25rem' }}>{title}</p>}
          <p style={{ margin: 0, color: '#374151', fontSize: '0.9rem', lineHeight: 1.6 }}>{children}</p>
        </div>
      </div>
    </div>
  );
};

const RefundPolicy = () => {
  const navigate = useNavigate();

  return (
    <div style={{ background: '#FFFBEB', minHeight: '100vh', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>

      <div style={{ background: '#ffffff', borderBottom: '1px solid #E5E9F0', position: 'sticky', top: 0, zIndex: 50 }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 1.5rem', height: '60px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <button onClick={() => navigate('/')} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', background: 'none', border: 'none', cursor: 'pointer', color: '#1A6FD6', fontWeight: 600, fontSize: '0.875rem' }}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2"><path d="M10 12L6 8l4-4" strokeLinecap="round" strokeLinejoin="round"/></svg>
            Back to Successlane
          </button>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#1A6FD6' }}></div>
            <span style={{ fontWeight: 700, color: '#0D1F3C', fontSize: '0.9rem' }}>Successlane</span>
          </div>
        </div>
      </div>

      <div style={{ maxWidth: '900px', margin: '0 auto', padding: '2.5rem 1.5rem' }}>

        {/* Header */}
        <div style={{ marginBottom: '2rem' }}>
          <p style={{ fontSize: '0.8125rem', fontWeight: 700, color: '#1A6FD6', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '0.5rem' }}>Legal</p>
          <h1 style={{ fontSize: '2rem', fontWeight: 800, color: '#0D1F3C', marginBottom: '0.5rem', lineHeight: 1.2 }}>Refund Policy</h1>
          <p style={{ color: '#6B7280', fontSize: '0.9rem' }}>Last updated: {LAST_UPDATED} &nbsp;·&nbsp; Operated by <strong>Success Lane</strong></p>
        </div>

        <Card>
          <Highlight type="success" title="The 100-Day Quality Guarantee">
            We stand behind our work. If you implement the rewrite we deliver and follow our LinkedIn Playbook for 100 days and don't see at least a 2× increase in recruiter calls and a 2× increase in recruiter profile visits, we will refund your full payment.
          </Highlight>

          <Section title="1. Our refund philosophy">
            <P>Successlane is a human-effort service. The moment you pay, a domain-matched recruiter is allocated, your intake is processed, and the rewrite work begins. For that reason we do not offer open-ended, no-questions-asked refunds.</P>
            <P>What we do offer is a clear, results-based guarantee: if you genuinely use what we deliver and the agreed measurable outcomes don't materialise, we refund you in full. This policy is written so that both you and we know exactly where we stand.</P>
          </Section>

          <Section title="2. When you are eligible for a refund">
            <p style={{ fontWeight: 700, color: '#0D1F3C', marginBottom: '0.4rem' }}>2.1 Cancellation within 30 minutes</p>
            <P>If you cancel your order within <strong>30 minutes of successful payment</strong> by writing to <a href="mailto:contact@successlane.io" style={{ color: '#1A6FD6' }}>contact@successlane.io</a>, you will receive a full refund. See our <button onClick={() => navigate('/cancellation')} style={{ background: 'none', border: 'none', padding: 0, color: '#1A6FD6', cursor: 'pointer', textDecoration: 'underline', font: 'inherit' }}>Cancellation Policy</button> for the full details.</P>

            <p style={{ fontWeight: 700, color: '#0D1F3C', marginBottom: '0.4rem', marginTop: '1rem' }}>2.2 The 100-Day Quality Guarantee</p>
            <P>If you have implemented the deliverables we provided and the measurable outcomes below are not met within 100 days of you implementing them, you are eligible for a full refund of the amount you paid.</P>
            <P><strong>Measurable outcomes:</strong></P>
            <BulletList items={[
              'At least a 2× increase in recruiter calls compared to the 30-day period immediately before you implemented the rewrite, and',
              'At least a 2× increase in recruiter profile visits compared to the 30-day period immediately before you implemented the rewrite.',
            ]} />

            <p style={{ fontWeight: 700, color: '#0D1F3C', marginBottom: '0.4rem', marginTop: '1rem' }}>2.3 Non-delivery on our part</p>
            <P>If Successlane is unable to deliver your order due to our own operational failure — for example, no suitable recruiter can be sourced for your domain, or a verifiable technical error on our end prevents delivery — we will issue a full refund.</P>

            <p style={{ fontWeight: 700, color: '#0D1F3C', marginBottom: '0.4rem', marginTop: '1rem' }}>2.4 Duplicate or accidental payment</p>
            <P>If you are charged twice for the same order, or a payment is processed in error, the duplicate amount will be refunded in full once verified with our payment partner Razorpay.</P>
          </Section>

          <Section title="3. Conditions for the 100-Day Quality Guarantee">
            <P>To be eligible for the 100-day refund, you must meet <strong>all</strong> of the following conditions:</P>
            <BulletList items={[
              'You have published the LinkedIn rewrite we delivered to your live LinkedIn profile within 7 days of receiving it, and the rewrite has remained substantially unchanged on your profile for the full 100-day evaluation period.',
              'You have actively followed the strategy laid out in the LinkedIn Playbook delivered alongside your rewrite — including the keyword placement, headline/summary structure, content cadence, and outreach scripts.',
              'You have completed the full 100-day period starting from the date you implemented the rewrite, not from the date of payment.',
              'You can demonstrate the above with proof — either through screenshots of your LinkedIn profile, activity, and analytics, or through a live screen-share with our team over a scheduled online meeting.',
              'You raise the refund request within 30 days of the 100-day evaluation period ending.',
            ]} />
            <Highlight type="warning" title="Why these conditions exist">
              The guarantee is tied to genuine implementation, not just purchase. A profile that was never published, a Playbook that was never followed, or a strategy that was abandoned after a week cannot fairly be tested against a 2× outcome target.
            </Highlight>
          </Section>

          <Section title="4. How to claim a refund">
            <P>To raise any refund request, follow these steps:</P>
            <BulletList items={[
              'Email contact@successlane.io with the subject line "Refund request — [your order ID]".',
              'In the body, briefly state which clause you are claiming under (e.g., 30-minute cancellation, 100-day guarantee, non-delivery, duplicate payment).',
              'For 100-day guarantee claims, attach the supporting evidence (LinkedIn profile screenshots, recruiter-call log, profile-visit analytics from LinkedIn) or request a screen-share session.',
              'Our team will acknowledge your request within 1 business day and respond with a decision within 5–7 business days.',
            ]} />
          </Section>

          <Section title="5. How refunds are processed">
            <P>Approved refunds are processed back to the original payment method through our payment partner Razorpay.</P>
            <BulletList items={[
              'UPI / Wallets: typically 1–3 business days after approval.',
              'Credit / Debit cards: typically 5–7 business days after approval, depending on your card issuer.',
              'Net Banking: typically 3–5 business days after approval.',
            ]} />
            <P>You will receive an email confirmation once the refund has been initiated. The exact time it takes to reflect in your account is governed by your bank or card issuer and is outside our control.</P>
          </Section>

          <Section title="6. What is not covered">
            <P>The following situations do not qualify for a refund:</P>
            <BulletList items={[
              'A change of mind after the 30-minute cancellation window has elapsed and outside the 100-day Quality Guarantee conditions.',
              'Outcomes that depend on factors outside our control — your overall experience, the state of the job market, individual employer or recruiter preferences, or the way you use the rewrite after delivery.',
              'Requests where you have materially altered the rewrite on your live profile or stopped following the Playbook strategy during the 100-day period.',
              'Requests based on false or incomplete information supplied to us at intake.',
              'Any service or component delivered by a third party (e.g., LinkedIn itself, Razorpay, Bright Data, WhatsApp).',
            ]} />
          </Section>

          <Section title="7. Contact us about a refund">
            <P>For any refund-related question, write to <a href="mailto:contact@successlane.io" style={{ color: '#1A6FD6' }}>contact@successlane.io</a> or call <a href="tel:+917259444336" style={{ color: '#1A6FD6' }}>+91 72594 44336</a> during working hours (Mon–Sat, 9:00 AM – 6:00 PM IST).</P>
          </Section>
        </Card>

        {/* Footer */}
        <div style={{ borderTop: '1px solid #E5E9F0', paddingTop: '2rem', marginTop: '2.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
          <p style={{ color: '#9CA3AF', fontSize: '0.8125rem' }}>© 2025 Success Lane · Successlane</p>
          <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap' }}>
            <button onClick={() => navigate('/cancellation')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#1A6FD6', fontSize: '0.8125rem', fontWeight: 500 }}>Cancellation</button>
            <button onClick={() => navigate('/terms')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#1A6FD6', fontSize: '0.8125rem', fontWeight: 500 }}>Terms</button>
            <button onClick={() => navigate('/privacy')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#1A6FD6', fontSize: '0.8125rem', fontWeight: 500 }}>Privacy</button>
            <button onClick={() => navigate('/')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6B7280', fontSize: '0.8125rem' }}>Back to Home</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RefundPolicy;
